import { Col, ConfirmModal, Row } from '@/components';
import { getGarageBySubProjects } from '@/features/mold/utils';
import { checkEnabledInputReflectedAmount } from '@/features/project/_utils/inspection-evaluation';
import type { ExpenditureCartModel } from '@/features/project/projects/_models/expenditure-cart';
import {
  InspectionEvaluationRsModel,
  type ProjectModel,
} from '@/features/project/projects/_models/project';
import { useDeleteExpenditureCart } from '@/features/project/projects/detail/income-and-expenditure/_hooks/expenditure-cart';
import { useId } from '@/hooks';
import { useMessage } from '@/providers';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import type { z } from 'zod';
import { ExpenditureCartFormDrawer } from '../../../income-and-expenditure/_components/expenditure-carts/ExpenditureCartFormDrawer';
import { RsExpenditureCartsTable } from '../../../income-and-expenditure/_components/expenditure-carts/RsExpenditureCartsTable';
import { ExpenditureItemFormDrawer } from '../../../income-and-expenditure/_components/expenditure-items/ExpenditureItemFormDrawer';

type Project = z.infer<typeof ProjectModel>;

type Props = Pick<
  Project,
  'inspectionEvaluation' | 'subProjects' | 'fiscalYear'
> & {
  editable: Project['isLocked'];
};

export const RsBudgetAmountsTable: FC<Props> = ({
  inspectionEvaluation,
  subProjects,
  fiscalYear,
  editable,
}) => {
  const { t } = useTranslation();
  const { openMessage } = useMessage();
  const { id: projectId } = useId();
  const [expenditureCartDelete, setExpenditureCartDelete] = useState<z.infer<
    typeof ExpenditureCartModel
  > | null>(null);

  const { mutateAsync: deleteExpenditureCartAsync } =
    useDeleteExpenditureCart();

  const garage = getGarageBySubProjects(subProjects);

  return (
    <>
      <Row>
        <Col span={24}>
          <RsExpenditureCartsTable
            data={garage?.expenditureCarts ?? []}
            projectId={projectId}
            subProjectId={garage?.id ?? ''}
            onClickDelete={(value) => {
              setExpenditureCartDelete(value);
            }}
            editable={editable}
          />
        </Col>
      </Row>

      <ExpenditureCartFormDrawer data={garage} />

      <ConfirmModal
        open={expenditureCartDelete !== null}
        title={t('delete-confirmation')}
        disclaimerText={t('delete-item-confirmation', {
          item: t('expenditure-cart'),
        })}
        onOk={async () => {
          invariant(expenditureCartDelete, t('unreachable-code'));

          try {
            await deleteExpenditureCartAsync({
              id: expenditureCartDelete.id,
              projectId,
              subProjectId: expenditureCartDelete.subProjectId,
            });
            const { reflectionStatusBasedOnOpinion } =
              InspectionEvaluationRsModel.partial().parse(inspectionEvaluation);
            const enabledInputReflectedAmount =
              checkEnabledInputReflectedAmount(
                reflectionStatusBasedOnOpinion?.action,
              );
            await Promise.all([
              enabledInputReflectedAmount
                ? openMessage(
                    'warning',
                    t('warning-message-action-completed-expenditure-cart', {
                      action: t('delete'),
                    }),
                  )
                : openMessage('success', t('message-delete-completed')),
              (async () => {
                setExpenditureCartDelete(null);
              })(),
            ]);
          } catch {
            await openMessage('error', t('message-delete-failed'));
          }
        }}
        onCancel={() => {
          setExpenditureCartDelete(null);
        }}
        skipCheck
      />
      <ExpenditureItemFormDrawer fiscalYear={fiscalYear} data={garage} />
    </>
  );
};
